/**
 * The phase-5 payroll lifecycle as an ADDITIVE, pure model (ADR 0010 section 9).
 *
 *     Draft → Validation → Calculated → Review → Approval → Finalized → Released
 *
 * WHAT THIS IS. A vocabulary and a transition table that can be tested without a database, plus an
 * explicit mapping from the states a run carries TODAY (`PAYROLL_RUN_STATES`) to the stage and the
 * canonical state each one corresponds to. It exists so that phase 5's routes, the stepper and the
 * downstream readers can be written against one definition instead of re-deriving it.
 *
 * WHAT THIS IS NOT. It renames nothing and migrates nothing. Persisted run states stay exactly as
 * `PAYROLL_RUN_STATES` lists them; `approved` keeps its meaning and `approvedCalculationVersionId`
 * keeps being what every reader treats as final. No route or worker consults this module, and it
 * adds no finalize or release transition to the API. ADR 0012 (the 4b / phase 5 boundary) is
 * explicit that phase 5 is not implemented to satisfy a 4b gate, and that 4b stops at the EXISTING
 * `approved` state.
 *
 * WHAT IS DECIDED, AND WHAT IS NOT. Where ADR 0010 section 9 states a fact, the model pins it and
 * says so. Where the ADR is silent or the choice belongs to the owner, the model does NOT choose:
 * it takes a `LifecycleConfig` flag, or it lists the point in `PHASE5_OPEN_DECISIONS`, and the
 * default is the ADR text where the ADR speaks and today's behaviour where it does not. Every edge
 * carries its `basis` — `adr` (stated in the ADR), `current_behaviour` (what the API and worker do
 * today, verified against `payroll-runs.ts` and the calculation worker) or `proposed` (needed to
 * keep the table connected, stated nowhere) — so a reviewer can see exactly what is opinion.
 *
 * UPDATE (ADR 0014, owner decisions of 2026-09-21): six of the open decisions below are now DECIDED and
 * carry a `decided` record; the persisted states are EXTENDED with `finalized` and `released`, and the
 * API's finalize, release and output-track routes are built on this module (`canAdvancePayslip`,
 * `canAdvancePayment`, the transition tables). `DECIDED_LIFECYCLE_CONFIG` is the configuration the API
 * implements. The comments below describe the model as first written; where one says nothing consults
 * it or that finalize/release are absent, ADR 0014 supersedes it.
 *
 * RN-SAFE: no Node built-ins, no I/O, no dates.
 */

import type { PayrollRunState } from './enums.js';

// ---------------------------------------------------------------------------
// Stages and states
// ---------------------------------------------------------------------------

/** The seven stages of the ADR's headline sentence, in order. */
export const LIFECYCLE_STAGES = [
  'draft',
  'validation',
  'calculated',
  'review',
  'approval',
  'finalized',
  'released',
] as const;
export type LifecycleStage = (typeof LIFECYCLE_STAGES)[number];

/**
 * The canonical states. The first eight are the ADR's table; `changes_requested`, `reopened` and
 * `cancelled` are its "plus ... as today" branch states.
 *
 * `approved` is NOT in the ADR table. It exists only under `finalizeIsSeparateAction` — the reading
 * in which the run is approved before it is finalized — and is inert otherwise. It is here so that
 * choice can be represented without pre-empting it.
 */
export const LIFECYCLE_STATES = [
  'draft',
  'validating',
  'calculating',
  'calculated',
  'in_review',
  'pending_approval',
  'approved',
  'finalized',
  'released',
  'changes_requested',
  'reopened',
  'cancelled',
] as const;
export type LifecycleState = (typeof LIFECYCLE_STATES)[number];

/** Branch states hang off the main path and belong to no stage. */
export const LIFECYCLE_BRANCH_STATES = ['changes_requested', 'reopened', 'cancelled'] as const;
export type LifecycleBranchState = (typeof LIFECYCLE_BRANCH_STATES)[number];

const STAGE_OF: Readonly<Record<LifecycleState, LifecycleStage | null>> = {
  draft: 'draft',
  validating: 'validation',
  // The stage in which the calculation is produced. The ADR places `calculating` between
  // `validating` and `calculated` without naming its stage; see the `calculating-stage` decision.
  calculating: 'calculated',
  calculated: 'calculated',
  in_review: 'review',
  pending_approval: 'approval',
  approved: 'approval',
  finalized: 'finalized',
  released: 'released',
  changes_requested: null,
  reopened: null,
  cancelled: null,
};

/** The stage a state belongs to, or `null` for a branch state. */
export function stageOf(state: LifecycleState): LifecycleStage | null {
  return STAGE_OF[state];
}

export function isBranchState(state: LifecycleState): state is LifecycleBranchState {
  return (LIFECYCLE_BRANCH_STATES as readonly string[]).includes(state);
}

// ---------------------------------------------------------------------------
// Configuration — the undecided points that change the shape of the table
// ---------------------------------------------------------------------------

export interface LifecycleConfig {
  /**
   * Whether finalizing is its own action after approval (`pending_approval → approved →
   * finalized`) or the approval IS the finalization (`pending_approval → finalized`).
   *
   * ADR 0010 section 9 lists `finalized` as REPLACING `approved`, which is the `false` reading and
   * the default. ADR 0012 calls `approved` "existing" and finalize "a phase 5 capability", which is
   * compatible with either. OWNER DECISION `finalize-action`.
   */
  readonly finalizeIsSeparateAction: boolean;
  /**
   * Whether a run must pass through `in_review` before it can be submitted. Today a run is
   * submitted straight from its calculation (`review_ready`); the ADR adds `in_review` as "new"
   * without saying it is mandatory. Default `false`. OWNER DECISION `review-stage-required`.
   */
  readonly reviewStageRequired: boolean;
  /**
   * Whether a FINALIZED run may be reopened (the edge `finalized → reopened`). ADR 0010 section 9 says
   * it may, until an output has been executed; OWNER DECISION D4 (ADR 0014) is that it may not. Kept as a
   * flag rather than deleting the edge so the ADR text remains representable. Absent means `true`.
   */
  readonly reopenAfterFinalize?: boolean;
}

/** The ADR text where it speaks, today's behaviour where it does not. Not a decision. */
export const DEFAULT_LIFECYCLE_CONFIG: LifecycleConfig = {
  finalizeIsSeparateAction: false,
  reviewStageRequired: false,
  reopenAfterFinalize: true,
};

/**
 * The lifecycle the owner DECIDED (ADR 0014): finalize is its own action after approval, review is not
 * mandatory (undecided, so today's behaviour), and a finalized run cannot be reopened. This is what
 * the API implements; `DEFAULT_LIFECYCLE_CONFIG` stays the ADR-text reading for comparison.
 */
export const DECIDED_LIFECYCLE_CONFIG: LifecycleConfig = {
  finalizeIsSeparateAction: true,
  reviewStageRequired: false,
  reopenAfterFinalize: false,
};

/**
 * The persisted run states in which the run's calculation version, results and rules snapshot are FINAL (ADR 0014, D2):
 * nothing recalculates, re-locks, reopens or re-approves them. `released` is `finalized` plus the release.
 */
export const FINAL_RUN_STATES = ['finalized', 'released'] as const;
export type FinalRunState = (typeof FINAL_RUN_STATES)[number];

export function isFinalRunState(state: string): state is FinalRunState {
  return (FINAL_RUN_STATES as readonly string[]).includes(state);
}

/** The states that exist under a configuration. `approved` only exists when finalize is separate. */
export function lifecycleStatesFor(
  config: LifecycleConfig = DEFAULT_LIFECYCLE_CONFIG,
): readonly LifecycleState[] {
  return LIFECYCLE_STATES.filter((s) => s !== 'approved' || config.finalizeIsSeparateAction);
}

// ---------------------------------------------------------------------------
// Transitions
// ---------------------------------------------------------------------------

export const LIFECYCLE_TRIGGERS = [
  'lock_inputs',
  'calculate',
  'calculation_complete',
  'start_review',
  'submit',
  'approve',
  'request_changes',
  'finalize',
  'release',
  'reopen',
] as const;
export type LifecycleTrigger = (typeof LIFECYCLE_TRIGGERS)[number];

/**
 * Where an edge comes from:
 *   - `adr`               — stated in ADR 0010 section 9.
 *   - `current_behaviour` — what the API or worker does today.
 *   - `proposed`          — stated nowhere; present so the table stays connected. Not a decision.
 */
export type LifecycleEdgeBasis = 'adr' | 'current_behaviour' | 'proposed';

export interface LifecycleTransition {
  readonly from: LifecycleState;
  readonly to: LifecycleState;
  readonly trigger: LifecycleTrigger;
  readonly basis: LifecycleEdgeBasis;
  readonly note?: string;
}

const t = (
  from: LifecycleState,
  to: LifecycleState,
  trigger: LifecycleTrigger,
  basis: LifecycleEdgeBasis,
  note?: string,
): LifecycleTransition => ({ from, to, trigger, basis, ...(note ? { note } : {}) });

/**
 * The transition table under a configuration.
 *
 * `cancelled` has NO inbound edge: nothing in the API sets it today (it exists in the enum and the
 * stepper renders it), and the ADR says only "as today". Which states may be cancelled, by whom and
 * with what effect is OWNER DECISION `cancel-rules`, so the model neither invents the edges nor
 * removes the state.
 */
export function lifecycleTransitions(
  config: LifecycleConfig = DEFAULT_LIFECYCLE_CONFIG,
): readonly LifecycleTransition[] {
  const edges: LifecycleTransition[] = [
    // -- Locking inputs: `lockInputs` accepts draft, reopened, changes_requested and review_ready.
    t('draft', 'validating', 'lock_inputs', 'current_behaviour'),
    t('reopened', 'validating', 'lock_inputs', 'current_behaviour'),
    t('changes_requested', 'validating', 'lock_inputs', 'current_behaviour'),
    t(
      'calculated',
      'validating',
      'lock_inputs',
      'current_behaviour',
      'lock again after a calculation',
    ),

    // -- Calculating: `calculate` refuses only submitted and approved, and needs a snapshot.
    t('validating', 'calculating', 'calculate', 'current_behaviour'),
    t('calculated', 'calculating', 'calculate', 'current_behaviour', 'recalculate'),
    t('changes_requested', 'calculating', 'calculate', 'current_behaviour'),
    t('reopened', 'calculating', 'calculate', 'current_behaviour'),
    t('calculating', 'calculated', 'calculation_complete', 'current_behaviour'),
    // A FAILED calculation does not change the persisted state away from `review_ready`; see
    // `deriveLifecycleState`, which reads it as `validating` from the version's own state.
    t('calculating', 'validating', 'calculation_complete', 'proposed', 'the calculation failed'),

    // -- Review.
    t('calculated', 'in_review', 'start_review', 'adr', '`in_review` is new in ADR 0010 section 9'),
    t('in_review', 'calculating', 'calculate', 'proposed', 'recalculate after review'),
    t('in_review', 'validating', 'lock_inputs', 'proposed', 'lock again after review'),

    // -- Submitting for approval. Today straight from the calculation.
    t('in_review', 'pending_approval', 'submit', 'adr'),
    t(
      'changes_requested',
      'pending_approval',
      'submit',
      'current_behaviour',
      'resubmit the same calculation',
    ),

    // -- Rejection.
    t('pending_approval', 'changes_requested', 'request_changes', 'current_behaviour'),

    // -- Release.
    t('finalized', 'released', 'release', 'adr', 'requires the run to be finalized'),

    // -- Reopen. Refused once any output has been executed; see `reopenBlockedReason`.
    t('pending_approval', 'reopened', 'reopen', 'current_behaviour'),
  ];

  if (!config.reviewStageRequired) {
    edges.push(
      t(
        'calculated',
        'pending_approval',
        'submit',
        'current_behaviour',
        'submitted straight from `review_ready`',
      ),
    );
  }

  if (config.finalizeIsSeparateAction) {
    edges.push(
      t('pending_approval', 'approved', 'approve', 'current_behaviour'),
      t('approved', 'finalized', 'finalize', 'adr', 'the finalize action'),
      t('approved', 'reopened', 'reopen', 'current_behaviour'),
    );
    if (config.reopenAfterFinalize !== false) {
      edges.push(
        t(
          'finalized',
          'reopened',
          'reopen',
          'adr',
          'a finalized run may be reopened until an output has been executed',
        ),
      );
    }
  } else {
    edges.push(
      t('pending_approval', 'finalized', 'approve', 'adr', '`finalized` replaces `approved`'),
    );
    if (config.reopenAfterFinalize !== false) {
      edges.push(
        t('finalized', 'reopened', 'reopen', 'current_behaviour', 'today: approved → reopened'),
      );
    }
  }

  return edges;
}

/** The states reachable from `from` in one step. */
export function nextLifecycleStates(
  from: LifecycleState,
  config: LifecycleConfig = DEFAULT_LIFECYCLE_CONFIG,
): readonly LifecycleState[] {
  return [
    ...new Set(
      lifecycleTransitions(config)
        .filter((e) => e.from === from)
        .map((e) => e.to),
    ),
  ];
}

export function canTransition(
  from: LifecycleState,
  to: LifecycleState,
  config: LifecycleConfig = DEFAULT_LIFECYCLE_CONFIG,
): boolean {
  return lifecycleTransitions(config).some((e) => e.from === from && e.to === to);
}

/** `cancelled` has no outbound edge; `released` has none either (a reopen from it is undecided). */
export function isTerminal(
  state: LifecycleState,
  config: LifecycleConfig = DEFAULT_LIFECYCLE_CONFIG,
): boolean {
  return nextLifecycleStates(state, config).length === 0;
}

/** ADR: "a reopen is refused once any output has been executed (the existing `paymentInitiatedAt` rule)". */
export function reopenBlockedReason(facts: { outputExecuted: boolean }): string | null {
  return facts.outputExecuted
    ? 'An output of this run has already been executed. Reopening would not undo it — raise a correction or a reversal instead.'
    : null;
}

// ---------------------------------------------------------------------------
// The persisted states, mapped
// ---------------------------------------------------------------------------

export interface PersistedStateMapping {
  /** The canonical state the persisted one corresponds to under `DEFAULT_LIFECYCLE_CONFIG`. */
  readonly state: LifecycleState;
  readonly stage: LifecycleStage | null;
  /** Whether ADR 0010 section 9's table states this mapping ("Replaces / extends"). */
  readonly statedByAdr: boolean;
  readonly note: string;
}

/**
 * Every persisted state and where the ADR table puts it. The mapping is total over
 * `PAYROLL_RUN_STATES` — a test fails if a persisted state is added without a row here.
 *
 * READ THIS TABLE AS A TRANSLATION, NOT A RENAME. The string a run carries does not change, and
 * neither does what any reader of it does. `approved → finalized` is the ADR's own row; whether it
 * survives (rename), or `approved` stays and `finalized` is added after it (extend), is OWNER
 * DECISION `state-model`, and `mapPersistedState` takes the configuration that decides it.
 */
export const PERSISTED_STATE_MAPPING: Readonly<Record<PayrollRunState, PersistedStateMapping>> = {
  draft: { state: 'draft', stage: 'draft', statedByAdr: true, note: 'unchanged' },
  inputs_locked: {
    state: 'validating',
    stage: 'validation',
    statedByAdr: true,
    note: 'ADR: `validating` replaces `inputs_locked`. Note the ADR says diagnostics run and clear HERE; today they are produced by the calculation, after this state.',
  },
  calculating: {
    state: 'calculating',
    stage: 'calculated',
    statedByAdr: true,
    note: 'unchanged; transient',
  },
  review_ready: {
    state: 'calculated',
    stage: 'calculated',
    statedByAdr: true,
    note: 'ADR: `calculated` replaces `review_ready`. A FAILED calculation also leaves the run here — use `deriveLifecycleState`, which reads the version.',
  },
  submitted: {
    state: 'pending_approval',
    stage: 'approval',
    statedByAdr: true,
    note: 'ADR: replaces `submitted`',
  },
  approved: {
    state: 'finalized',
    stage: 'finalized',
    statedByAdr: true,
    note: 'ADR: `finalized` replaces `approved`. Under `finalizeIsSeparateAction` this is `approved` instead.',
  },
  finalized: {
    state: 'finalized',
    stage: 'finalized',
    statedByAdr: true,
    note: 'ADDED by ADR 0014 D1 (extended, not renamed): persisted after `approved`, by the finalize action.',
  },
  released: {
    state: 'released',
    stage: 'released',
    statedByAdr: true,
    note: 'ADDED by ADR 0014 D1: persisted after `finalized`, by the release action (per run, D3).',
  },
  changes_requested: {
    state: 'changes_requested',
    stage: null,
    statedByAdr: true,
    note: 'branch, "as today"',
  },
  reopened: { state: 'reopened', stage: null, statedByAdr: true, note: 'branch, "as today"' },
  cancelled: {
    state: 'cancelled',
    stage: null,
    statedByAdr: true,
    note: 'branch, "as today"; nothing sets it yet',
  },
};

/**
 * The canonical state for a persisted one under `config`.
 *
 * Only `approved` depends on the configuration: it is `finalized` under the ADR's table, and stays
 * `approved` when finalize is a separate action.
 */
export function mapPersistedState(
  persisted: PayrollRunState,
  config: LifecycleConfig = DEFAULT_LIFECYCLE_CONFIG,
): LifecycleState {
  if (persisted === 'approved' && config.finalizeIsSeparateAction) return 'approved';
  return PERSISTED_STATE_MAPPING[persisted].state;
}

/**
 * The persisted states a canonical state can be read from. `in_review`, `released` and (unless
 * finalize is separate) `approved` have NO persisted counterpart — the ADR calls the first two "new",
 * and they are the states phase 5 would have to persist. This is the inverse a migration would need,
 * and it is empty exactly where a migration would have to decide something.
 */
export function persistedStatesFor(
  state: LifecycleState,
  config: LifecycleConfig = DEFAULT_LIFECYCLE_CONFIG,
): readonly PayrollRunState[] {
  return (Object.keys(PERSISTED_STATE_MAPPING) as PayrollRunState[]).filter(
    (p) => mapPersistedState(p, config) === state,
  );
}

/** What is needed beyond the persisted state to place a run exactly. */
export interface PersistedRunFacts {
  readonly state: PayrollRunState;
  /** The state of the run's CURRENT calculation version, or null when there is none. */
  readonly currentCalculationState?:
    'queued' | 'running' | 'ready' | 'failed' | 'superseded' | null;
  readonly currentCalculationReconciled?: boolean;
}

/**
 * The canonical state of a run, refined by its calculation where the persisted state is ambiguous.
 *
 * `review_ready` is set both when a calculation finishes and when it FAILS (the worker returns the
 * run there so HR can lock again or recalculate). The ADR defines `calculated` as "a reconciled
 * calculation version exists", so a `review_ready` run whose current version did not finish or did
 * not reconcile is `validating`, not `calculated`. Where the version is not supplied the table
 * answer is returned unchanged.
 */
export function deriveLifecycleState(
  facts: PersistedRunFacts,
  config: LifecycleConfig = DEFAULT_LIFECYCLE_CONFIG,
): LifecycleState {
  const mapped = mapPersistedState(facts.state, config);
  if (facts.state !== 'review_ready' || facts.currentCalculationState === undefined) return mapped;
  const usable =
    facts.currentCalculationState === 'ready' && facts.currentCalculationReconciled !== false;
  return usable ? 'calculated' : 'validating';
}

// ---------------------------------------------------------------------------
// The two output tracks — monotonic
// ---------------------------------------------------------------------------

/**
 * Payslips: publication to employees. Needs no payment provider and can complete on its own.
 * ADR: `not_published → published (per run, and per employee for a correction)`.
 */
export const PAYSLIP_STATUSES = ['not_published', 'published'] as const;
export type PayslipStatus = (typeof PAYSLIP_STATUSES)[number];

/**
 * Payments: execution. `file_generated` (a bank file the company uploads itself) is a valid
 * terminal path when no provider is connected; `submitted` and `paid` exist only where one is.
 */
export const PAYMENT_STATUSES = [
  'not_started',
  'pending',
  'file_generated',
  'submitted',
  'paid',
  'partially_paid',
  'failed',
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

export interface TrackTransition<S extends string> {
  readonly from: S;
  readonly to: S;
  readonly basis: LifecycleEdgeBasis;
  readonly note?: string;
}

export const PAYSLIP_TRANSITIONS: readonly TrackTransition<PayslipStatus>[] = [
  { from: 'not_published', to: 'published', basis: 'adr' },
];

/**
 * The ADR's diagram draws `partially_paid | failed` from `file_generated`. It does not say whether
 * a provider-connected payment can also end there from `submitted`, which it obviously can; that
 * edge is `proposed`. Whether `failed` can be retried and whether `partially_paid` can be completed
 * is not stated, so the model has NO edge out of either (OWNER DECISION `payment-recovery`).
 */
export const PAYMENT_TRANSITIONS: readonly TrackTransition<PaymentStatus>[] = [
  { from: 'not_started', to: 'pending', basis: 'adr' },
  { from: 'pending', to: 'file_generated', basis: 'adr' },
  {
    from: 'file_generated',
    to: 'submitted',
    basis: 'adr',
    note: 'only where a provider is connected',
  },
  { from: 'submitted', to: 'paid', basis: 'adr', note: 'only where a provider is connected' },
  { from: 'file_generated', to: 'partially_paid', basis: 'adr' },
  { from: 'file_generated', to: 'failed', basis: 'adr' },
  { from: 'submitted', to: 'partially_paid', basis: 'proposed' },
  { from: 'submitted', to: 'failed', basis: 'proposed' },
];

/**
 * A rank per status, used ONLY to state "neither can move backwards" as a property: every
 * transition above must raise it, and every pair that does not is refused. The three outcomes at
 * the end share a rank because none of them follows another.
 */
export const PAYSLIP_RANK: Readonly<Record<PayslipStatus, number>> = {
  not_published: 0,
  published: 1,
};
export const PAYMENT_RANK: Readonly<Record<PaymentStatus, number>> = {
  not_started: 0,
  pending: 1,
  file_generated: 2,
  submitted: 3,
  paid: 4,
  partially_paid: 4,
  failed: 4,
};

/**
 * ADR: "`released` requires the run to be `finalized`; publishing and paying each require
 * `released`." The section's introduction says the two operations act on a "finalized-or-released"
 * run, which the later, precise bullet narrows; this follows the bullet. Which run state is meant
 * when a run is `released` per LEGAL ENTITY rather than per run is OWNER DECISION `release-scope`.
 */
export function runAllowsOutputs(state: LifecycleState): boolean {
  return state === 'released';
}

export function canAdvancePayslip(
  from: PayslipStatus,
  to: PayslipStatus,
  run: { state: LifecycleState },
): boolean {
  return (
    runAllowsOutputs(run.state) && PAYSLIP_TRANSITIONS.some((e) => e.from === from && e.to === to)
  );
}

export function canAdvancePayment(
  from: PaymentStatus,
  to: PaymentStatus,
  run: { state: LifecycleState },
): boolean {
  return (
    runAllowsOutputs(run.state) && PAYMENT_TRANSITIONS.some((e) => e.from === from && e.to === to)
  );
}

// ---------------------------------------------------------------------------
// What is NOT decided
// ---------------------------------------------------------------------------

export interface Phase5OpenDecision {
  readonly id: string;
  readonly question: string;
  /** Where the question is raised, or where the text that makes it a question lives. */
  readonly source: string;
  /** What this model assumes so that it is usable, until the owner decides. */
  readonly modelDefault: string;
  /** The `LifecycleConfig` key that represents it, when one does. */
  readonly configKey?: keyof LifecycleConfig;
  /** Set once the owner has decided it (ADR 0014, ADR 0015). An undecided point has no `decided`. */
  readonly decided?: {
    readonly decision: string;
    /**
     * Where it was decided: the Phase 5 lifecycle brief, recorded in ADR 0014, or the payment service
     * owner decisions of 2026-09-22, recorded in ADR 0015.
     */
    readonly recordedIn: 'ADR 0014' | 'ADR 0015';
  };
}

/**
 * Every point the model leaves open. None of these is settled by the ADRs; each is an OWNER decision,
 * and the ids are stable so a decision record can cite them.
 */
export const PHASE5_OPEN_DECISIONS: readonly Phase5OpenDecision[] = [
  {
    id: 'state-model',
    question:
      'Are persisted run states RENAMED (`approved` → `finalized`, `submitted` → `pending_approval`, ...) or EXTENDED (the current states stay and new ones are added)?',
    source:
      'ADR 0010 section 9 table ("Replaces / extends"); ADR 0012 "the EXISTING `approved` state"',
    modelDefault:
      'Nothing is renamed or migrated. `PERSISTED_STATE_MAPPING` is a translation table, not a migration.',
    decided: {
      decision:
        'EXTENDED: every current state keeps its name and meaning; finalized and released are added as persisted states, with additive columns and tables (D1).',
      recordedIn: 'ADR 0014',
    },
  },
  {
    id: 'finalize-action',
    question: 'Is finalize a separate action after approval, or is approval the finalization?',
    source:
      'ADR 0010 section 9 ("`finalized` replaces `approved`") vs ADR 0012 ("finalize ... phase 5 capabilities")',
    modelDefault: '`finalizeIsSeparateAction: false` (the ADR table).',
    configKey: 'finalizeIsSeparateAction',
    decided: {
      decision:
        'Finalize is a distinct, separately permissioned lock action after approval: pending_approval to approved to finalized (D2). DECIDED_LIFECYCLE_CONFIG.finalizeIsSeparateAction is true.',
      recordedIn: 'ADR 0014',
    },
  },
  {
    id: 'review-stage-required',
    question: 'Must a run pass through `in_review` before it can be submitted?',
    source: 'ADR 0010 section 9 (`in_review` is "new"; not stated as mandatory)',
    modelDefault: '`reviewStageRequired: false` (today submits straight from the calculation).',
    configKey: 'reviewStageRequired',
  },
  {
    id: 'release-scope',
    question:
      'Is `released` per run, or per legal entity for organisations that run several entities in one cycle?',
    source: 'ADR 0010 "Not decided here"',
    modelDefault: 'Per run. Nothing here models an entity-level release.',
    decided: {
      decision: 'Per run (D3).',
      recordedIn: 'ADR 0014',
    },
  },
  {
    id: 'final-for-readers',
    question:
      'Which state do downstream consumers treat as "final" — reports, filings, Form 16, GL, the variance baseline, the retroactive-publish guard?',
    source:
      'ADR 0010 section 9; ADR 0011 decision 6; more than twenty readers key on `approvedCalculationVersionId`',
    modelDefault:
      'No reader is repointed. The readers keep treating `approvedCalculationVersionId` as final; this module is not consulted by any of them.',
    decided: {
      decision:
        'Decided per consumer with evidence (ADR 0014, Consumers). Payslip and payment operate from finalized and released only; GL posting requires finalized; every other reader of approvedCalculationVersionId is unchanged.',
      recordedIn: 'ADR 0014',
    },
  },
  {
    id: 'released-reopen',
    question: 'May a `released` run be reopened when no output has been executed?',
    source: 'ADR 0010 section 9 ("a reopen is refused once any output has been executed")',
    modelDefault: 'No reopen edge out of `released`.',
    configKey: 'reopenAfterFinalize',
    decided: {
      decision:
        'No. A released run cannot be un-released and a finalized run cannot be reopened; a correction is a new run or adjustment, none of which is built (D4).',
      recordedIn: 'ADR 0014',
    },
  },
  {
    id: 'output-executed',
    question:
      'What counts as an output having been "executed" for the reopen rule: a payment initiated, a bank file generated, a payslip published?',
    source: 'ADR 0010 section 9 ("the existing `paymentInitiatedAt` rule")',
    modelDefault: 'The caller supplies `outputExecuted`; the model does not derive it.',
    decided: {
      decision:
        'Moot for reopen: a reopen is refused from finalization onward, whatever has been executed (D4). The paymentInitiatedAt rule still applies before finalization.',
      recordedIn: 'ADR 0014',
    },
  },
  {
    id: 'cancel-rules',
    question: 'From which states may a run be cancelled, by whom, and with what effect?',
    source: 'ADR 0010 section 9 ("`cancelled` as today"); nothing sets it today',
    modelDefault: '`cancelled` exists and is terminal, with no inbound edge.',
  },
  {
    id: 'calculating-stage',
    question: 'Which stage does the transient `calculating` state belong to?',
    source: 'ADR 0010 section 9 (states listed, stages not mapped)',
    modelDefault: '`calculated` — the stage in which the calculation is produced.',
  },
  {
    id: 'diagnostics-timing',
    question:
      'Do diagnostics run and clear in Validation (before calculation), as the ADR says, or after the calculation, as they do today?',
    source: 'ADR 0010 section 9 (`validating`) vs the calculation worker',
    modelDefault: "Not modelled; today's order is unchanged.",
  },
  {
    id: 'payment-recovery',
    question: 'Can a `failed` payment be retried and a `partially_paid` one completed?',
    source: 'ADR 0010 section 9 diagram (both are drawn as ends)',
    modelDefault: 'No edge out of either. `submitted → partially_paid | failed` is `proposed`.',
    decided: {
      decision:
        'Retries are modelled as payment ATTEMPTS on an obligation (payroll-payments.ts), not as edges out of the output track. The output track stays monotonic with no edge out of failed; a retryable failure is carried by the obligation and is not written to the output row; partially_paid remains a derived run-level outcome (D3).',
      recordedIn: 'ADR 0015',
    },
  },
];
